import React, { useState, useMemo, useEffect } from 'react';
import { useQuery, useQueries, useMutation, useQueryClient } from '@tanstack/react-query';
import { Search, ChevronUp, ChevronDown, RotateCcw, Save, LayoutDashboard } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { ROLE_TEMPLATES, type Permission } from '@/types/permissions';
import { MASTER_DASHBOARD_CARDS, type DashboardCardConfig } from '@/config/dashboardLayouts';
import { useApp } from '@/contexts/AppContext';
import { getRoles, getRoleWithPermissions } from '@/api/services/roleService';
import { getDashboardLayout, saveDashboardLayout, deleteDashboardLayout } from '@/api/services/dashboardLayoutService';
import { toast } from 'sonner';

// ── Storage helpers ──────────────────────────────────────────────────────────

const STORAGE_KEY = 'dashboard-role-layouts-v2';

// One-time migration: clear any old cached layout keys so they don't interfere
try {
  localStorage.removeItem('dashboard-role-layouts');
  localStorage.removeItem('dashboard-role-layouts-v1');
} catch (_) {}

export interface RoleCardOrder {
  cardId: string;
  order: number;
}

function loadSavedLayouts(): Record<string, RoleCardOrder[]> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

export function saveLayoutForRole(roleId: string, cards: RoleCardOrder[]): void {
  const all = loadSavedLayouts();
  all[roleId] = cards;
  localStorage.setItem(STORAGE_KEY, JSON.stringify(all));
}

function clearLayoutForRole(roleId: string): void {
  const all = loadSavedLayouts();
  delete all[roleId];
  localStorage.setItem(STORAGE_KEY, JSON.stringify(all));
}

export function loadLayoutForRole(roleId: string): RoleCardOrder[] | null {
  const all = loadSavedLayouts();
  return all[roleId] ?? null;
}

// ── Tier labels ──────────────────────────────────────────────────────────────

const TIER_LABELS: Record<DashboardCardConfig['minWidth'], string> = {
  mini: 'Mini Cards',
  medium: 'Medium Cards',
  full: 'Full-Width Cards',
};

const TIER_DESCRIPTIONS: Record<DashboardCardConfig['minWidth'], string> = {
  mini: 'Small KPI tiles — appear in a row at the top of the dashboard',
  medium: 'Half-width cards — appear in pairs side by side',
  full: 'Full-width cards — each occupies the entire dashboard width',
};

// ── Card label lookup (human-readable names) ─────────────────────────────────

function getCardLabel(cardId: string): string {
  const labels: Record<string, string> = {
    'view_ceo_cash_position_card': 'CEO Cash Position',
    'view_executive_cash_position_card': 'Executive Cash Position',
    'view_current_cash_mini_card': 'Current Cash Balance',
    'view_30day_forecast_mini_card': '30-Day Forecast',
    'view_overdue_invoices_mini_card': 'Overdue Invoices',
    'view_upcoming_payments_mini_card': 'Upcoming Payments',
    'view_variance_card': 'Variance',
    'view_uncategorized_items_card': 'Uncategorised Items',
    'view_bank_accounts_card': 'Bank Accounts Breakdown',
    'view_upcoming_recurring_transactions_card': 'Upcoming Recurring Transactions',
    'view_actions_card': 'Actions',
    'view_board_ready_summary_card': 'Board-Ready Summary',
    'view_critical_alerts_card': 'Critical Alerts',
    'view_governance_compliance_card': 'Governance & Compliance',
    'view_scenario_comparison_card': 'Scenario Comparison',
    'view_recent_transactions_card': 'Recent Transactions',
    'view_90day_interactive_chart_card': '90-Day Forecast Chart',

  };
  return labels[cardId] ?? cardId;
}

// ── Tier ordering ────────────────────────────────────────────────────────────

const TIER_ORDER: DashboardCardConfig['minWidth'][] = ['mini', 'medium', 'full'];

// ── Component ────────────────────────────────────────────────────────────────

interface WorkingCard {
  cardId: string;
  minWidth: DashboardCardConfig['minWidth'];
  order: number;
  ThumbnailComponent?: React.ComponentType<{ isHovered?: boolean; alwaysVisible?: boolean }>;
}

export function AdminDashboardLayout() {
  const { mode } = useApp();
  const queryClient = useQueryClient();
  const [roleSearch, setRoleSearch] = useState('');
  const [workingCards, setWorkingCards] = useState<WorkingCard[]>([]);
  const [isDirty, setIsDirty] = useState(false);
  const [hoveredCard, setHoveredCard] = useState<string | null>(null);

  // Live mode: fetch roles from API
  const { data: liveRolesData } = useQuery({
    queryKey: ['roles'],
    queryFn: getRoles,
    enabled: mode === 'live',
  });

  // Live mode: fetch permissions for all roles to filter by dashboard access
  const allRoleIds = useMemo(
    () => (liveRolesData?.roles ?? []).map(r => r.roleId),
    [liveRolesData]
  );

  const allRolePermissionsResults = useQueries({
    queries: mode === 'live'
      ? allRoleIds.map(roleId => ({
          queryKey: ['role-permissions', roleId],
          queryFn: () => getRoleWithPermissions(roleId),
          staleTime: 5 * 60 * 1000,
        }))
      : [],
  });

  // Set of all dashboard card permissions from the master config
  const dashboardCardPermissions = useMemo(
    () => new Set(
      MASTER_DASHBOARD_CARDS
        .filter(c => c.enabled && !(c.demoOnly && mode !== 'demo'))
        .map(c => c.permission)
    ),
    [mode]
  );

  // Build a unified role list — demo uses ROLE_TEMPLATES, live uses API
  const roles = useMemo(() => {
    if (mode === 'live') {
      return (liveRolesData?.roles ?? []).filter((r, idx) => {
        const permResult = allRolePermissionsResults[idx];
        if (!permResult?.data) return false;
        return permResult.data.permissions.some(
          p => p.isEnabled && dashboardCardPermissions.has(p.permissionKey)
        );
      }).map(r => ({
        id: String(r.roleId),
        name: r.roleName,
        description: r.description ?? '',
        permissions: [] as string[],
      })).sort((a, b) => a.name.localeCompare(b.name));
    }
    return [...ROLE_TEMPLATES.filter(r => r.id !== 'regional' && r.id !== 'admin')].sort((a, b) => a.name.localeCompare(b.name));
  }, [mode, liveRolesData, allRolePermissionsResults, dashboardCardPermissions]);

  const [selectedRoleId, setSelectedRoleId] = useState<string>('');

  // Derive the effective selected role ID synchronously so the first
  // role is highlighted immediately — without waiting for a useEffect.
  const effectiveRoleId = useMemo(() => {
    if (selectedRoleId && roles.some(r => r.id === selectedRoleId)) {
      return selectedRoleId;
    }
    return roles.length > 0 ? roles[0].id : '';
  }, [selectedRoleId, roles]);

  // Keep selectedRoleId in sync so downstream effects see the right value
  useEffect(() => {
    if (effectiveRoleId && effectiveRoleId !== selectedRoleId) {
      setSelectedRoleId(effectiveRoleId);
    }
  }, [effectiveRoleId, selectedRoleId]);

  // Filter roles by search
  const visibleRoles = useMemo(() => {
    return roles.filter(r =>
      r.name.toLowerCase().includes(roleSearch.toLowerCase())
    );
  }, [roleSearch, roles]);

  const selectedRole = useMemo(
    () => roles.find(r => r.id === effectiveRoleId),
    [effectiveRoleId, roles]
  );

  // Live mode: fetch saved layout for selected role
  const numericRoleId = mode === 'live' ? Number(effectiveRoleId) : undefined;
  const { data: liveLayoutData } = useQuery({
    queryKey: ['dashboard-layout', numericRoleId],
    queryFn: () => getDashboardLayout(numericRoleId as number),
    enabled: mode === 'live' && !!numericRoleId,
  });

  // Live mode: fetch permissions for the selected role
  const { data: liveRolePermissionsData } = useQuery({
    queryKey: ['role-permissions', numericRoleId],
    queryFn: () => getRoleWithPermissions(numericRoleId as number),
    enabled: mode === 'live' && !!numericRoleId,
  });

  const liveRolePermissions = useMemo(() => {
    if (!liveRolePermissionsData) return null;
    return new Set(
      liveRolePermissionsData.permissions
        .filter(p => p.isEnabled)
        .map(p => p.permissionKey)
    );
  }, [liveRolePermissionsData]);

  // Build working card list for the selected role
  // NOTE: selectedRole is intentionally excluded from deps — in live mode,
  // useQueries returns a new array each render, causing roles (and thus
  // selectedRole) to be a fresh object reference every time.  Including it
  // would re-run this effect on every render, resetting workingCards and
  // isDirty and making arrow-button reordering impossible.  We derive the
  // role data we need from stable sources inside the effect instead.
  useEffect(() => {
    if (!effectiveRoleId) return;

    let permittedCards: DashboardCardConfig[];
    if (mode === 'demo') {
      const demoRole = ROLE_TEMPLATES.find(r => r.id === effectiveRoleId);
      if (!demoRole) return;
      const rolePermissions = new Set<string>(demoRole.permissions as string[]);
      permittedCards = MASTER_DASHBOARD_CARDS.filter(
        c => c.enabled && rolePermissions.has(c.permission)
      );
    } else {
      if (!liveRolePermissions) return;
      permittedCards = MASTER_DASHBOARD_CARDS.filter(
        c => c.enabled && !c.demoOnly && liveRolePermissions.has(c.permission)
      );
    }

    // Check for a saved custom order
    const saved = mode === 'live'
      ? (liveLayoutData?.cards?.map(c => ({ cardId: c.cardId, order: c.order })) ?? null)
      : loadLayoutForRole(effectiveRoleId);

    let ordered: WorkingCard[];

    if (saved && saved.length > 0) {
      const savedMap = new Map(saved.map(s => [s.cardId, s.order]));
      const permittedIds = new Set(permittedCards.map(c => c.id));

      // Place new cards at the end of their size tier
      const newCards = permittedCards.filter(c => !savedMap.has(c.id));
      for (const card of newCards) {
        const maxInTier = permittedCards
          .filter(c => c.minWidth === card.minWidth && savedMap.has(c.id))
          .reduce((max, c) => Math.max(max, savedMap.get(c.id) ?? 0), 0);
        savedMap.set(card.id, maxInTier + 10);
      }

      ordered = permittedCards
        .map(c => ({
          cardId: c.id,
          minWidth: c.minWidth,
          order: savedMap.get(c.id) ?? c.order + 1000,
          ThumbnailComponent: undefined,
        }))
        .sort((a, b) => a.order - b.order);

      // Prune stale entries and persist if the layout changed
      const hasStalEntries = saved.some(s => !permittedIds.has(s.cardId));
      if (mode === 'demo' && (hasStalEntries || newCards.length > 0)) {
        saveLayoutForRole(effectiveRoleId, ordered.map(c => ({ cardId: c.cardId, order: c.order })));
      }
    } else {
      ordered = permittedCards
        .map(c => ({
          cardId: c.id,
          minWidth: c.minWidth,
          order: c.order,
          ThumbnailComponent: undefined,
        }))
        .sort((a, b) => a.order - b.order);
    }

    setWorkingCards(ordered);
    setIsDirty(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveRoleId, mode, liveLayoutData, liveRolePermissions]);

  // Cards grouped by tier
  const cardsByTier = useMemo(() => {
    const groups: Record<DashboardCardConfig['minWidth'], WorkingCard[]> = {
      mini: [],
      medium: [],
      full: [],
    };
    for (const card of workingCards) {
      groups[card.minWidth].push(card);
    }
    // Sort each tier by order so arrows reflect visual position
    for (const tier of Object.keys(groups) as Array<keyof typeof groups>) {
      groups[tier].sort((a, b) => a.order - b.order);
    }
    return groups;
  }, [workingCards]);

  function moveCard(cardId: string, direction: 'up' | 'down') {
    setWorkingCards(prev => {
      const card = prev.find(c => c.cardId === cardId);
      if (!card) return prev;

      // Derive tier cards fresh from current state — avoids stale closure on cardsByTier
      const tierCards = prev
        .filter(c => c.minWidth === card.minWidth)
        .sort((a, b) => a.order - b.order);

      const idx = tierCards.findIndex(c => c.cardId === cardId);

      if (direction === 'up' && idx === 0) return prev;
      if (direction === 'down' && idx === tierCards.length - 1) return prev;

      const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
      const swapCard = tierCards[swapIdx];

      // Swap orders
      return prev.map(c => {
        if (c.cardId === cardId) return { ...c, order: swapCard.order };
        if (c.cardId === swapCard.cardId) return { ...c, order: card.order };
        return c;
      });
    });
    setIsDirty(true);
  }

  // Save mutation for live mode
  const saveMutation = useMutation({
    mutationFn: (cards: RoleCardOrder[]) =>
      saveDashboardLayout(Number(effectiveRoleId), { cards: cards.map(c => ({ cardId: c.cardId, order: c.order })) }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dashboard-layout', Number(effectiveRoleId)] });
    },
  });

  // Delete mutation for live mode
  const deleteMutation = useMutation({
    mutationFn: () => deleteDashboardLayout(Number(effectiveRoleId)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dashboard-layout', Number(effectiveRoleId)] });
    },
  });

  function handleSave() {
    const toSave: RoleCardOrder[] = workingCards.map(c => ({
      cardId: c.cardId,
      order: c.order,
    }));

    if (mode === 'live') {
      saveMutation.mutate(toSave, {
        onSuccess: () => {
          setIsDirty(false);
          toast.success(`Dashboard layout saved for ${selectedRole?.name}`);
        },
        onError: () => {
          toast.error('Failed to save dashboard layout');
        },
      });
    } else {
      saveLayoutForRole(effectiveRoleId, toSave);
      setIsDirty(false);
      toast.success(`Dashboard layout saved for ${selectedRole?.name}`);
    }
  }

  function handleReset() {
    if (!selectedRole) return;

    if (mode === 'live') {
      deleteMutation.mutate(undefined, {
        onSuccess: () => {
          rebuildDefaultCards();
          toast.success(`Layout reset to default for ${selectedRole.name}`);
        },
        onError: () => {
          toast.error('Failed to reset dashboard layout');
        },
      });
    } else {
      clearLayoutForRole(effectiveRoleId);
      rebuildDefaultCards();
      toast.success(`Layout reset to default for ${selectedRole.name}`);
    }
  }

  function rebuildDefaultCards() {
    if (!selectedRole) return;
    let permittedCards: DashboardCardConfig[];
    if (mode === 'demo') {
      const rolePermissions = new Set<string>(selectedRole.permissions as string[]);
      permittedCards = MASTER_DASHBOARD_CARDS.filter(
        c => c.enabled && rolePermissions.has(c.permission)
      );
    } else {
      if (!liveRolePermissions) return;
      permittedCards = MASTER_DASHBOARD_CARDS.filter(
        c => c.enabled && !c.demoOnly && liveRolePermissions.has(c.permission)
      );
    }
    const defaultCards: WorkingCard[] = permittedCards
      .map(c => ({
        cardId: c.id,
        minWidth: c.minWidth,
        order: c.order,
        ThumbnailComponent: undefined,
      }))
      .sort((a, b) => a.order - b.order);

    setWorkingCards(defaultCards);
    setIsDirty(false);
  }

  return (
    <div className="flex h-full min-h-0">
      {/* ── Left panel — role list ─────────────────────────────────────── */}
      <div className="w-64 flex-none border-r flex flex-col bg-muted/20">
        <div className="p-4 border-b">
          <h2 className="font-semibold text-sm mb-3">Roles</h2>
          <div className="relative">
            <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-muted-foreground" />
            <Input
              placeholder="Search roles..."
              value={roleSearch}
              onChange={e => setRoleSearch(e.target.value)}
              className="pl-8 h-8 text-sm"
            />
          </div>
        </div>
        <div className="flex-1 overflow-y-auto">
          {visibleRoles.length === 0 ? (
            <p className="text-xs text-muted-foreground p-4">No roles match your search.</p>
          ) : (
            visibleRoles.map(role => (
              <button
                key={role.id}
                type="button"
                onClick={() => {
                  if (isDirty) {
                    if (!window.confirm('You have unsaved changes. Discard them?')) return;
                  }
                  setSelectedRoleId(role.id);
                }}
                className={`w-full text-left px-4 py-3 text-sm border-b transition-colors hover:bg-muted/50 ${
                  effectiveRoleId === role.id
                    ? 'bg-primary/10 border-l-2 border-l-primary font-medium'
                    : 'border-l-2 border-l-transparent'
                }`}
              >
                <div className="font-medium">{role.name}</div>
                <div className="text-xs text-muted-foreground mt-0.5">{role.description}</div>
              </button>
            ))
          )}
        </div>
      </div>

      {/* ── Right panel — layout editor ───────────────────────────────── */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {selectedRole ? (
          <>
            {/* Header */}
            <div className="px-6 py-4 border-b flex items-start justify-between gap-4">
              <div>
                <div className="flex items-center gap-2">
                  <LayoutDashboard className="h-4 w-4 text-muted-foreground" />
                  <h2 className="font-semibold">Dashboard Layout — {selectedRole.name}</h2>
                  {isDirty && (
                    <Badge variant="outline" className="text-xs text-amber-600 border-amber-300">
                      Unsaved changes
                    </Badge>
                  )}
                </div>
                <p className="text-sm text-muted-foreground mt-1">
                  Reorder cards within each tier using the arrows. Changes apply to all users with this role.
                </p>
              </div>
              <div className="flex items-center gap-2 flex-none">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleReset}
                  className="gap-1.5"
                >
                  <RotateCcw className="h-3.5 w-3.5" />
                  Reset to Default
                </Button>
                <Button
                  size="sm"
                  onClick={handleSave}
                  disabled={!isDirty}
                  className="gap-1.5"
                >
                  <Save className="h-3.5 w-3.5" />
                  Save Layout
                </Button>
              </div>
            </div>

            {/* Card tiers */}
            <div className="flex-1 overflow-y-auto p-6 space-y-8">
              {TIER_ORDER.map(tier => {
                const tierCards = cardsByTier[tier];
                if (tierCards.length === 0) return null;

                return (
                  <div key={tier}>
                    <div className="mb-3">
                      <h3 className="font-medium text-sm">{TIER_LABELS[tier]}</h3>
                      <p className="text-xs text-muted-foreground mt-0.5">{TIER_DESCRIPTIONS[tier]}</p>
                    </div>
                    <div className="space-y-2">
                      {tierCards.map((card, idx) => (
                        <div
                          key={card.cardId}
                          className="flex items-center gap-3 p-3 rounded-lg border bg-card hover:bg-muted/30 transition-colors"
                          onMouseEnter={() => setHoveredCard(card.cardId)}
                          onMouseLeave={() => setHoveredCard(null)}
                        >
                          {/* Sequence badge */}
                          <div className="w-6 h-6 rounded-full bg-primary/10 text-primary text-xs font-semibold flex items-center justify-center flex-none">
                            {idx + 1}
                          </div>

                          {/* Card label */}
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium">{getCardLabel(card.cardId)}</p>
                            <p className="text-xs text-muted-foreground capitalize">{tier} card</p>
                          </div>

                          {/* Up / Down arrows */}
                          <div className="flex flex-col gap-0.5 flex-none">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6"
                              disabled={idx === 0}
                              onClick={() => moveCard(card.cardId, 'up')}
                              title="Move up"
                            >
                              <ChevronUp className="h-3.5 w-3.5" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6"
                              disabled={idx === tierCards.length - 1}
                              onClick={() => moveCard(card.cardId, 'down')}
                              title="Move down"
                            >
                              <ChevronDown className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                    {tier !== 'full' && <Separator className="mt-8" />}
                  </div>
                );
              })}

              {workingCards.length === 0 && (
                <div className="flex flex-col items-center justify-center py-16 text-center">
                  <LayoutDashboard className="h-10 w-10 text-muted-foreground/30 mb-3" />
                  <p className="text-sm font-medium text-muted-foreground">No dashboard cards assigned</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    This role has no dashboard card permissions. Grant permissions in Roles &amp; Permissions first.
                  </p>
                </div>
              )}
            </div>
          </>
        ) : (
          <div className="flex items-center justify-center h-full text-muted-foreground text-sm">
            Select a role to configure its dashboard layout
          </div>
        )}
      </div>
    </div>
  );
}
